import { useState } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  Tabs,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  mchApi,
  URINE_PROTEIN,
  useChildren,
  useMchAction,
  useMchConfig,
  usePregnancies,
  type Child,
  type GrowthReference,
  type Pregnancy,
} from '@/features/mch/api';
import { usePatients } from '@/features/patients/api';
import { PageContainer } from '@/layouts/PageContainer';
import { useUrlState } from '@/lib/url-state';
import { dayLabel } from '@/pages/calendar/time';
import { clinicDayLabel } from '@/pages/ops/mc-format';
import styles from '@/pages/ops/parity.module.css';
import { FormDialog } from './FormDialog';
import { clinicDate } from '@/lib/clinic-time';

type Tab = 'anc' | 'growth' | 'vacc';
type Open =
  | { kind: 'start' }
  | { kind: 'visit'; p: Pregnancy }
  | { kind: 'close'; p: Pregnancy }
  | { kind: 'growth'; c: Child }
  | { kind: 'vaccine'; c: Child }
  | { kind: 'approve' }
  | null;

const today = () => clinicDate();
const ageLabel = (m: number) => `${Math.floor(m / 12)} years ${m % 12} months`;

/**
 * Mothers & children (Admin v2): antenatal records with visits and the
 * pre-eclampsia flag, growth against the configured reference, and child
 * vaccinations against the configured schedule. All data from /mch; the
 * parameters are shown as pending clinical sign-off until approved.
 */
export function MothersChildrenPage() {
  const { hasPermission } = useSession();
  const canManage = hasPermission('mothers_children:manage');
  const [tab, setTab] = useUrlState<Tab>('tab', 'anc', [
    'anc',
    'growth',
    'vacc',
  ]);
  const [open, setOpen] = useState<Open>(null);
  const cfg = useMchConfig();
  const close = () => setOpen(null);
  return (
    <PageContainer>
      {cfg.data && !cfg.data.approved && (
        <div className={styles.notice} role="status">
          <strong>
            Flags, growth reference and vaccine schedule are pending clinical
            sign-off.
          </strong>{' '}
          {cfg.data.source}
          {canManage && (
            <>
              {' '}
              <Button size="sm" onClick={() => setOpen({ kind: 'approve' })}>
                Record sign-off
              </Button>
            </>
          )}
        </div>
      )}
      {cfg.data?.approved && (
        <p className={styles.infoNotice}>
          Signed off by {cfg.data.approvedBy} on{' '}
          {cfg.data.approvedAt ? clinicDayLabel(cfg.data.approvedAt) : '—'}.
        </p>
      )}
      <Tabs
        variant="bar"
        label="Mothers & children"
        value={tab}
        onChange={setTab}
        items={[
          { value: 'anc', label: 'Antenatal' },
          { value: 'growth', label: 'Growth' },
          { value: 'vacc', label: 'Child vaccines' },
        ]}
      />
      {tab === 'anc' && <Antenatal canManage={canManage} onOpen={setOpen} />}
      {tab === 'growth' && <Growth canManage={canManage} onOpen={setOpen} />}
      {tab === 'vacc' && <Vaccines canManage={canManage} onOpen={setOpen} />}
      {open?.kind === 'start' && <StartDialog onClose={close} />}
      {open?.kind === 'visit' && <VisitDialog p={open.p} onClose={close} />}
      {open?.kind === 'close' && <CloseDialog p={open.p} onClose={close} />}
      {open?.kind === 'growth' && <GrowthDialog c={open.c} onClose={close} />}
      {open?.kind === 'vaccine' && <VaccineDialog c={open.c} onClose={close} />}
      {open?.kind === 'approve' && <ApproveDialog onClose={close} />}
    </PageContainer>
  );
}

interface Props {
  canManage: boolean;
  onOpen: (o: Open) => void;
}

function Antenatal({ canManage, onOpen }: Props) {
  const q = usePregnancies();
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data)
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const c = q.data.config.antenatal;
  return (
    <>
      {canManage && (
        <div className={styles.rowActions}>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onOpen({ kind: 'start' })}
          >
            New antenatal record
          </Button>
        </div>
      )}
      {!q.data.rows.length && (
        <Card title="Antenatal">
          <EmptyState title="No open antenatal records" />
        </Card>
      )}
      {q.data.rows.map((p) => (
        <Card
          key={p.id}
          title={`${p.patient.fullName} · ${p.gravidaPara}`}
          flush
          actions={
            canManage && (
              <div className={styles.rowActions}>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => onOpen({ kind: 'visit', p })}
                >
                  Add visit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onOpen({ kind: 'close', p })}
                >
                  Close record
                </Button>
              </div>
            )
          }
        >
          <dl className={styles.kv} style={{ padding: '14px 18px' }}>
            <dt>Patient</dt>
            <dd>
              <Link to={`/patients/${p.patient.id}`}>{p.patient.fullName}</Link>
            </dd>
            <dt>Last period</dt>
            <dd>{dayLabel(p.lmp)}</dd>
            <dt>Due date</dt>
            <dd>
              <strong>{dayLabel(p.edd)}</strong>
            </dd>
            <dt>Today</dt>
            <dd>
              {p.gestation.weeks} weeks {p.gestation.days} days
            </dd>
            <dt>Risk</dt>
            <dd>
              {p.risks.map((r) => (
                <Badge key={r} tone="warning">
                  {r}
                </Badge>
              ))}{' '}
              {p.preEclampsiaFlag && (
                <Badge tone="danger">
                  Raised BP or protein: check for pre-eclampsia
                </Badge>
              )}
              {!p.risks.length && !p.preEclampsiaFlag && '—'}
            </dd>
          </dl>
          <DataTable
            caption={`Antenatal visits · ${p.patient.fullName}`}
            rows={p.visits}
            rowKey={(v) => v.id}
            empty={<EmptyState title="No visits yet" />}
            columns={[
              {
                key: 'd',
                header: 'Date',
                render: (v) => dayLabel(v.visitDate),
              },
              {
                key: 'w',
                header: 'Weeks',
                align: 'right',
                render: (v) => v.weeks,
              },
              {
                key: 'wt',
                header: 'Weight',
                align: 'right',
                render: (v) => `${v.weightKg} kg`,
              },
              {
                key: 'bp',
                header: 'BP',
                render: (v) => (
                  <>
                    {v.systolic}/{v.diastolic}{' '}
                    {v.flag && <Badge tone="danger">Flag</Badge>}
                  </>
                ),
              },
              {
                key: 'ur',
                header: 'Urine protein',
                render: (v) => v.urineProtein,
              },
              {
                key: 'fh',
                header: 'Fundal height',
                align: 'right',
                render: (v) => v.fundalHeightCm ?? '-',
              },
              {
                key: 'fhr',
                header: 'Fetal heart',
                align: 'right',
                render: (v) => v.fetalHeartRate ?? '-',
              },
              { key: 'n', header: 'Note', render: (v) => v.note ?? '' },
            ]}
          />
        </Card>
      ))}
      <p className={styles.cardFoot}>
        Flag at BP {c.bpFlag.systolic}/{c.bpFlag.diastolic} or above, or urine
        protein {c.proteinFlagFrom} or more. Due date = last period +{' '}
        {c.gestationDays} days.
      </p>
    </>
  );
}

function GrowthChart({
  c,
  reference,
}: {
  c: Child;
  reference: GrowthReference | null;
}) {
  const W = 420;
  const H = 220;
  const maxM = Math.max(36, ...c.growth.map((g) => g.ageMonths));
  const px = (m: number) => 40 + (m / maxM) * (W - 60);
  const py = (w: number) => H - 26 - ((w - 2) / (19 - 2)) * (H - 40);
  const line = (vals: number[], months: number[]) =>
    vals
      .map(
        (v, i) =>
          `${i ? 'L' : 'M'}${px(months[i]).toFixed(1)} ${py(v).toFixed(1)}`,
      )
      .join(' ');
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`Weight for age chart · ${c.patient.fullName}`}
      style={{ width: '100%', maxWidth: W }}
    >
      {reference && (
        <>
          <path
            d={line(reference.plus2, reference.months)}
            fill="none"
            stroke="var(--line)"
          />
          <path
            d={line(reference.median, reference.months)}
            fill="none"
            stroke="var(--muted)"
            strokeDasharray="4 3"
          />
          <path
            d={line(reference.minus2, reference.months)}
            fill="none"
            stroke="var(--line)"
          />
        </>
      )}
      {[0, 12, 24, 36].map((m) => (
        <text
          key={m}
          x={px(m)}
          y={H - 8}
          textAnchor="middle"
          fontSize="10"
          fill="var(--muted)"
        >
          {m} mo
        </text>
      ))}
      {[4, 8, 12, 16].map((w) => (
        <text
          key={w}
          x={30}
          y={py(w) + 3}
          textAnchor="end"
          fontSize="10"
          fill="var(--muted)"
        >
          {w} kg
        </text>
      ))}
      {c.growth.length > 1 && (
        <path
          d={line(
            c.growth.map((g) => g.weightKg),
            c.growth.map((g) => g.ageMonths),
          )}
          fill="none"
          stroke="var(--main)"
          strokeWidth="2"
        />
      )}
      {c.growth.map((g) => (
        <circle
          key={g.id}
          cx={px(g.ageMonths)}
          cy={py(g.weightKg)}
          r="3.5"
          fill="var(--main)"
        />
      ))}
    </svg>
  );
}

function Growth({ canManage, onOpen }: Props) {
  const q = useChildren();
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data)
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const ref = q.data.config.growthReference;
  if (!q.data.rows.length)
    return (
      <Card title="Growth">
        <EmptyState
          title={`No children under ${q.data.config.children.maxAgeYears} at this branch`}
        />
      </Card>
    );
  return (
    <>
      {q.data.rows.map((c) => {
        const reference =
          c.patient.sex === 'female'
            ? ref.girls
            : c.patient.sex === 'male'
              ? ref.boys
              : null;
        return (
          <Card
            key={c.patient.id}
            title={`${c.patient.fullName} · ${ageLabel(c.ageMonths)}`}
            flush
            actions={
              canManage && (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => onOpen({ kind: 'growth', c })}
                >
                  Add measurement
                </Button>
              )
            }
          >
            <div className={styles.grid2} style={{ padding: '14px 18px' }}>
              <div>
                <GrowthChart c={c} reference={reference} />
                <span className={styles.sub}>
                  {reference
                    ? `${ref.label} (${c.patient.sex === 'female' ? 'girls' : 'boys'}).`
                    : 'No approved reference for this child yet; only the measurements are shown.'}
                </span>
              </div>
              <DataTable
                caption={`Measurements · ${c.patient.fullName}`}
                rows={c.growth}
                rowKey={(g) => g.id}
                empty={<EmptyState title="No measurements yet" />}
                columns={[
                  {
                    key: 'd',
                    header: 'Date',
                    render: (g) => dayLabel(g.measuredAt),
                  },
                  {
                    key: 'm',
                    header: 'Age (months)',
                    align: 'right',
                    render: (g) => g.ageMonths,
                  },
                  {
                    key: 'w',
                    header: 'Weight',
                    align: 'right',
                    render: (g) => `${g.weightKg} kg`,
                  },
                  {
                    key: 'l',
                    header: 'Length / height',
                    align: 'right',
                    render: (g) => `${g.lengthCm} cm`,
                  },
                ]}
              />
            </div>
          </Card>
        );
      })}
    </>
  );
}

function Vaccines({ canManage, onOpen }: Props) {
  const q = useChildren();
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data)
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data.rows.length)
    return (
      <Card title="Child vaccines">
        <EmptyState
          title={`No children under ${q.data.config.children.maxAgeYears} at this branch`}
        />
      </Card>
    );
  return (
    <>
      {q.data.rows.map((c) => (
        <Card
          key={c.patient.id}
          title={`${c.patient.fullName} · vaccinations`}
          flush
          actions={
            canManage && (
              <Button
                size="sm"
                variant="primary"
                onClick={() => onOpen({ kind: 'vaccine', c })}
              >
                Record a dose given elsewhere
              </Button>
            )
          }
        >
          <DataTable
            caption={`Vaccinations · ${c.patient.fullName}`}
            rows={c.schedule}
            rowKey={(s) => s.vaccine}
            columns={[
              {
                key: 'v',
                header: 'Vaccine',
                render: (s) => <strong>{s.vaccine}</strong>,
              },
              { key: 'd', header: 'Due at', render: (s) => s.dueLabel },
              {
                key: 'g',
                header: 'Given',
                render: (s) =>
                  s.given ? (
                    dayLabel(s.given.givenAt)
                  ) : s.overdue ? (
                    <Badge tone="danger">Due since {dayLabel(s.dueAt)}</Badge>
                  ) : s.optional ? (
                    <Badge tone="neutral">Optional</Badge>
                  ) : (
                    `Due ${dayLabel(s.dueAt)}`
                  ),
              },
              {
                key: 'w',
                header: 'Where',
                render: (s) => s.given?.where ?? '-',
              },
            ]}
          />
          {!!c.givenHere.length && (
            <p className={styles.cardFoot}>
              Given at this clinic:{' '}
              {c.givenHere
                .map(
                  (g) =>
                    `${g.vaccine}${g.dose ? ` (${g.dose})` : ''} ${clinicDayLabel(g.at)}`,
                )
                .join(' · ')}
            </p>
          )}
        </Card>
      ))}
      <p className={styles.cardFoot}>
        National programme doses are usually given at a Klinik Kesihatan; record
        them here. Doses given at this clinic are recorded in the consultation.
      </p>
    </>
  );
}

// ---- drawers ---------------------------------------------------------------

function StartDialog({ onClose }: { onClose: () => void }) {
  const a = useMchAction(mchApi.start);
  const [search, setSearch] = useState('');
  const [patientId, setPatientId] = useState('');
  const [lmp, setLmp] = useState('');
  const [gp, setGp] = useState('G1 P0');
  const [risks, setRisks] = useState('');
  const found = usePatients({ q: search.trim() || undefined, pageSize: 8 });
  const options = (found.data?.data ?? []).map((p) => ({
    value: p.id,
    label: p.fullName,
  }));
  return (
    <FormDialog
      title="New antenatal record"
      onClose={onClose}
      pending={a.isPending}
      disabled={
        !patientId || !lmp || lmp > today() || !/^G\d{1,2} P\d{1,2}$/.test(gp)
      }
      onSubmit={() =>
        a.mutateAsync({
          patientId,
          lmp,
          gravidaPara: gp,
          risks: risks
            .split('\n')
            .map((r) => r.trim())
            .filter(Boolean),
        })
      }
    >
      <Field label="Find patient">
        <Input
          value={search}
          placeholder="Name or IC"
          onChange={(e) => setSearch(e.target.value)}
        />
      </Field>
      <Field label="Patient">
        <Select
          value={patientId}
          placeholder="Choose a patient"
          onChange={(e) => setPatientId(e.target.value)}
          options={options}
        />
      </Field>
      <Field label="Last menstrual period">
        <Input
          type="date"
          max={today()}
          value={lmp}
          onChange={(e) => setLmp(e.target.value)}
        />
      </Field>
      <Field label="Gravida / para (e.g. G2 P1)">
        <Input
          value={gp}
          maxLength={8}
          onChange={(e) => setGp(e.target.value)}
        />
      </Field>
      <Field label="Risks (one per line)">
        <textarea
          className={styles.textarea}
          rows={3}
          value={risks}
          onChange={(e) => setRisks(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}

function VisitDialog({ p, onClose }: { p: Pregnancy; onClose: () => void }) {
  const a = useMchAction(mchApi.visit);
  const [v, setV] = useState({
    visitDate: today(),
    weightKg: '',
    bp: '',
    urineProtein: 'Nil',
    fundal: '',
    fhr: '',
    note: '',
  });
  const bp = /^(\d{2,3})\/(\d{2,3})$/.exec(v.bp.trim());
  const invalid =
    !v.visitDate || v.visitDate > today() || !(Number(v.weightKg) >= 30) || !bp;
  return (
    <FormDialog
      title={`Antenatal visit · ${p.patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      disabled={invalid}
      submitLabel="Save visit"
      onSubmit={() =>
        a.mutateAsync({
          id: p.id,
          visitDate: v.visitDate,
          weightKg: Number(v.weightKg),
          systolic: Number(bp![1]),
          diastolic: Number(bp![2]),
          urineProtein: v.urineProtein,
          ...(v.fundal ? { fundalHeightCm: Number(v.fundal) } : {}),
          ...(v.fhr ? { fetalHeartRate: Number(v.fhr) } : {}),
          ...(v.note.trim() ? { note: v.note.trim() } : {}),
        })
      }
    >
      <Field label="Date">
        <Input
          type="date"
          max={today()}
          value={v.visitDate}
          onChange={(e) => setV({ ...v, visitDate: e.target.value })}
        />
      </Field>
      <Field label="Weight kg">
        <Input
          type="number"
          step="0.1"
          value={v.weightKg}
          onChange={(e) => setV({ ...v, weightKg: e.target.value })}
        />
      </Field>
      <Field label="BP">
        <Input
          value={v.bp}
          placeholder="120/80"
          onChange={(e) => setV({ ...v, bp: e.target.value })}
        />
      </Field>
      <Field label="Urine protein">
        <Select
          value={v.urineProtein}
          onChange={(e) => setV({ ...v, urineProtein: e.target.value })}
          options={URINE_PROTEIN.map((u) => ({ value: u, label: u }))}
        />
      </Field>
      <Field label="Fundal height (cm)">
        <Input
          type="number"
          value={v.fundal}
          onChange={(e) => setV({ ...v, fundal: e.target.value })}
        />
      </Field>
      <Field label="Fetal heart rate">
        <Input
          type="number"
          value={v.fhr}
          onChange={(e) => setV({ ...v, fhr: e.target.value })}
        />
      </Field>
      <Field label="Note">
        <textarea
          className={styles.textarea}
          rows={3}
          maxLength={500}
          value={v.note}
          onChange={(e) => setV({ ...v, note: e.target.value })}
        />
      </Field>
    </FormDialog>
  );
}

function CloseDialog({ p, onClose }: { p: Pregnancy; onClose: () => void }) {
  const a = useMchAction(mchApi.close);
  const [outcome, setOutcome] = useState('delivered');
  const [at, setAt] = useState(today());
  const [note, setNote] = useState('');
  return (
    <FormDialog
      title={`Close antenatal record · ${p.patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      disabled={!at || at > today()}
      submitLabel="Close record"
      onSubmit={() =>
        a.mutateAsync({
          id: p.id,
          outcome,
          at,
          ...(note.trim() ? { note: note.trim() } : {}),
        })
      }
    >
      <p className={styles.notice}>The record and its visits stay on file.</p>
      <Field label="Outcome">
        <Select
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          options={[
            { value: 'delivered', label: 'Delivered' },
            { value: 'miscarriage', label: 'Miscarriage' },
            { value: 'transferred', label: 'Care transferred' },
            { value: 'other', label: 'Other' },
          ]}
        />
      </Field>
      <Field label="Date">
        <Input
          type="date"
          max={today()}
          value={at}
          onChange={(e) => setAt(e.target.value)}
        />
      </Field>
      <Field label="Note">
        <Input
          value={note}
          maxLength={300}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}

function GrowthDialog({ c, onClose }: { c: Child; onClose: () => void }) {
  const a = useMchAction(mchApi.growth);
  const [v, setV] = useState({
    measuredAt: today(),
    weightKg: '',
    lengthCm: '',
  });
  const invalid =
    !v.measuredAt ||
    v.measuredAt > today() ||
    !(Number(v.weightKg) > 0) ||
    !(Number(v.lengthCm) >= 30);
  return (
    <FormDialog
      title={`Measurement · ${c.patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      disabled={invalid}
      onSubmit={() =>
        a.mutateAsync({
          patientId: c.patient.id,
          measuredAt: v.measuredAt,
          weightKg: Number(v.weightKg),
          lengthCm: Number(v.lengthCm),
        })
      }
    >
      <Field label="Date">
        <Input
          type="date"
          max={today()}
          min={c.dateOfBirth}
          value={v.measuredAt}
          onChange={(e) => setV({ ...v, measuredAt: e.target.value })}
        />
      </Field>
      <Field label="Weight kg">
        <Input
          type="number"
          step="0.1"
          value={v.weightKg}
          onChange={(e) => setV({ ...v, weightKg: e.target.value })}
        />
      </Field>
      <Field label="Length or height cm">
        <Input
          type="number"
          step="0.1"
          value={v.lengthCm}
          onChange={(e) => setV({ ...v, lengthCm: e.target.value })}
        />
      </Field>
    </FormDialog>
  );
}

function VaccineDialog({ c, onClose }: { c: Child; onClose: () => void }) {
  const a = useMchAction(mchApi.vaccine);
  const missing = c.schedule.filter((s) => !s.given);
  const [v, setV] = useState({
    vaccine: missing[0]?.vaccine ?? '',
    givenAt: '',
    where: 'Klinik Kesihatan',
    batch: '',
  });
  return (
    <FormDialog
      title={`Dose given elsewhere · ${c.patient.fullName}`}
      onClose={onClose}
      pending={a.isPending}
      disabled={
        !v.vaccine ||
        !v.givenAt ||
        v.givenAt > today() ||
        v.where.trim().length < 2
      }
      onSubmit={() =>
        a.mutateAsync({
          patientId: c.patient.id,
          vaccine: v.vaccine,
          givenAt: v.givenAt,
          where: v.where.trim(),
          ...(v.batch.trim() ? { batch: v.batch.trim() } : {}),
        })
      }
    >
      <Field label="Vaccine">
        <Select
          value={v.vaccine}
          onChange={(e) => setV({ ...v, vaccine: e.target.value })}
          options={missing.map((s) => ({
            value: s.vaccine,
            label: `${s.vaccine} · ${s.dueLabel}`,
          }))}
        />
      </Field>
      <Field label="Given on">
        <Input
          type="date"
          max={today()}
          min={c.dateOfBirth}
          value={v.givenAt}
          onChange={(e) => setV({ ...v, givenAt: e.target.value })}
        />
      </Field>
      <Field label="Where">
        <Input
          value={v.where}
          maxLength={80}
          onChange={(e) => setV({ ...v, where: e.target.value })}
        />
      </Field>
      <Field label="Batch (optional)">
        <Input
          value={v.batch}
          maxLength={40}
          onChange={(e) => setV({ ...v, batch: e.target.value })}
        />
      </Field>
    </FormDialog>
  );
}

function ApproveDialog({ onClose }: { onClose: () => void }) {
  const a = useMchAction(mchApi.approve);
  const [note, setNote] = useState('');
  return (
    <FormDialog
      title="Record clinical sign-off"
      onClose={onClose}
      pending={a.isPending}
      disabled={note.trim().length < 5}
      submitLabel="Record sign-off"
      onSubmit={() => a.mutateAsync({ note: note.trim() })}
    >
      <p className={styles.notice}>
        Confirms the antenatal flags, the growth reference and the vaccine
        schedule shown on this page.
      </p>
      <Field label="Sign-off note (who approved, reference)">
        <Input
          value={note}
          maxLength={300}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </FormDialog>
  );
}
